import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import ConditionalRender from '@Components/ConditionalRender/ConditionalRender';
import Icon from '@Components/Icon/Icon';
import { ICheckout } from '@Redux/modules/checkout/actions';
import { useCart } from '@Utils/context/CartContext';
import useCommerceProfile from '@Utils/hooks/useCommerceProfile';
import useTranslate from '@Utils/hooks/useTranslate';
import { BUY_HBB, BUY_WFBB } from '@Views/Map/Map';
import { useDispatch, useSelector } from 'react-redux';
import { modalTypes, setModal } from '@Redux/modules/modal/actions';
import { FormikProps } from 'formik';
import { IFormikFields } from '../ContactDetails/ContactDetails';
import {
	AccordionsContainer,
	CardContainer,
	ContactName,
	ContactWrapper,
	ContentWrapper,
	Data,
	HeaderDescriptionWrapper,
	HeaderTitle,
	HeaderWrapper,
	IconWrapper,
	ContactDetailWrapper,
	DataBold,
	DescriptionWrapper,
} from './ContactDisplayDetails.styled';
import EditCTA from '../ShippingDetails/components/EditCTA';
import AddCTA from '../ShippingDetails/components/AddCTA';
import { getcontactDetailsArgs, getHbbWfbbContactDetailsArgs } from '../ShippingDetails/util';
import { useOutlet } from '@Utils/context/OutletContext';

interface IContactDisplayDetailsProps {
	isSimplifiedCheckout?: boolean;
}

const ContactDisplayDetails: FunctionComponent<IContactDisplayDetailsProps> = (isSimplifiedCheckout) => {
	const { translate } = useTranslate();

	const GET_USER_PROFILE = 'GET_USER_PROFILE';
	const { contactDetails, updateProfile } = useCommerceProfile({
		busyIndicatorListener: GET_USER_PROFILE,
	});

	const { setUserContactDetails } = useOutlet();

	const ICON_SIZE = 32;
	const { cart } = useCart();
	const contactDetailsRef = useRef<FormikProps<IFormikFields>>(null);
	const hbbWfbbProcessCheckout = useSelector(
		(state: types.redux.IState) => state.persistentCheckout.hbbWfbbProcessCheckout
	);
	const [hasContactDetails, sethasContactDetails] = useState(false);

	const dispatch = useDispatch();

	const hbbWfbbItem = useMemo(() => {
		return cart.items.find((item) => [BUY_HBB, BUY_WFBB].includes(item.plan?.PA || ''));
	}, [cart]);
	const isWfbbProcess = useMemo(() => {
		return !!cart.items.find((item) => [BUY_WFBB].includes(item.plan?.PA || ''));
	}, [cart]);

	useEffect(() => {
		if (contactDetails && !hasContactDetails) {
			updateProfile({ contactDetails });
			setUserContactDetails({
				firstName: contactDetails?.fatherName || '',
				middleName: contactDetails?.middleName,
				lastName: contactDetails?.lastName,
				phoneNumber: contactDetails?.phoneNumber,
			});
			sethasContactDetails(true);
		}
	}, [contactDetails]);

	const orderIncludesPlan = cart.items.some(
		(item) => item.withPlan && ![BUY_HBB, BUY_WFBB].includes(item.plan?.PA || '')
	);

	const showContactForm = () => {
		dispatch(
			setModal({
				type: modalTypes.CONTACT_FORM,
				data: {
					contactDetailsRef,
					orderIncludesPlan,
					isHbbWfbbProcess: !!hbbWfbbItem,
					isWfbbProcess,
					isSimplifiedCheckout: true,
				},
				withoutContainer: true,
			})
		);
		return false;
	};

	const editItem = () => {
		showContactForm();
	};

	const addContact = () => {
		showContactForm();
	};

	function getTitle(contactDetails: ICheckout['payload']['contactDetails']) {
		const args = hbbWfbbItem
			? getHbbWfbbContactDetailsArgs(hbbWfbbProcessCheckout)
			: getcontactDetailsArgs(contactDetails);
		return translate(`shipping-details.accordion.contact-data.subtitle-${args.length}`, ...args);
	}
	const title = getTitle(contactDetails);

	return (
		<CardContainer>
			<HeaderWrapper>
				<Icon name="contact_icon" fill="black38" width={40} height={40} />
				<HeaderDescriptionWrapper>
					<HeaderTitle>{translate('contact-display-details.title')}</HeaderTitle>
				</HeaderDescriptionWrapper>
				<ConditionalRender
					show={!!contactDetails}
					onTrue={() => <EditCTA onClick={editItem} uniqueId="editContact" />}
				/>
			</HeaderWrapper>
			<ContentWrapper>
				{title !== translate(`shipping-details.accordion.contact-data.subtitle-0`) && (
					<AccordionsContainer>
						<ContactWrapper>
							<IconWrapper>
								<Icon name="person" fill="black38" width={ICON_SIZE} height={ICON_SIZE} />
							</IconWrapper>
							<DescriptionWrapper>
								<ContactName>{title}</ContactName>
								<ContactDetailWrapper>
									<ConditionalRender
										show={!!hbbWfbbItem}
										onTrue={() => (
											<>
												<ConditionalRender
													show={!!hbbWfbbProcessCheckout?.emailId}
													onTrue={() => (
														<Data>{String(hbbWfbbProcessCheckout?.emailId)}</Data>
													)}
												/>
												<ConditionalRender
													show={!!hbbWfbbProcessCheckout?.contactNumber1}
													onTrue={() => (
														<DataBold>{hbbWfbbProcessCheckout?.contactNumber1}</DataBold>
													)}
												/>
											</>
										)}
										onFalse={() => (
											<>
												<ConditionalRender
													show={!!contactDetails?.email}
													onTrue={() => <Data>{String(contactDetails?.email)}</Data>}
												/>
												<ConditionalRender
													show={!!contactDetails?.phoneNumber}
													onTrue={() => <DataBold>{contactDetails?.phoneNumber}</DataBold>}
												/>
											</>
										)}
									/>
								</ContactDetailWrapper>
							</DescriptionWrapper>
						</ContactWrapper>
					</AccordionsContainer>
				)}
				<ConditionalRender
					show={!contactDetails}
					onTrue={() => (
						<AddCTA
							label={translate('secure-checkout.add-new-contact')}
							onClick={addContact}
							uniqueId="addContact"
						/>
					)}
				/>
			</ContentWrapper>
		</CardContainer>
	);
};

export default ContactDisplayDetails;
